import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { LogOut } from 'lucide-react';
import { AppBar, Screen } from '@/components/layout';
import { Button, Card, Chip, ErrorNote, SectionTitle, Spinner } from '@/components/ui';
import { apiData } from '@/lib/api';
import { useAuth } from '@/store/auth';
import type { AdminSystemInfo } from '@/types';
import { formatBytes, formatCount, formatUptime } from './lib';

/** 서버·DB 관리 (§3.2) — 상태 점검과 안전한 유지보수 작업 */
export function AdminSystemPage() {
  const logout = useAuth((s) => s.logout);
  const { data, isPending, isError, refetch } = useQuery({
    queryKey: ['admin', 'system'],
    queryFn: () => apiData<AdminSystemInfo>('/admin/system'),
    refetchInterval: 30_000,
  });

  return (
    <Screen>
      <AppBar title="시스템" back />

      {isPending && <Spinner />}
      {isError && <ErrorNote message="시스템 정보를 불러오지 못했어요." onRetry={() => void refetch()} />}

      {data && (
        <div className="flex flex-col gap-4 p-4">
          <section>
            <SectionTitle>데이터베이스</SectionTitle>
            <Card className="flex flex-col gap-3">
              <div className="flex items-center gap-2">
                <Chip tone={data.db.ok ? 'primary' : 'accent'}>
                  {data.db.ok ? '정상' : '응답 없음'}
                </Chip>
                {data.db.latencyMs != null && (
                  <span className="text-sm text-ink-sub">왕복 {data.db.latencyMs}ms</span>
                )}
                <span className="text-sm text-ink-sub">· 크기 {formatBytes(data.db.sizeBytes)}</span>
              </div>

              {data.db.tables.length > 0 && (
                <div className="overflow-x-auto rounded-sm border border-line">
                  <table className="w-full min-w-[280px] text-sm">
                    <thead>
                      <tr className="bg-bg text-left text-ink-sub">
                        <th className="px-3 py-1.5 font-medium">테이블</th>
                        <th className="px-3 py-1.5 text-right font-medium">행 수(추정)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.db.tables.map((table) => (
                        <tr key={table.name} className="border-t border-line">
                          <td className="px-3 py-1.5">{table.name}</td>
                          <td className="px-3 py-1.5 text-right">{formatCount(table.rows)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </Card>
          </section>

          <section>
            <SectionTitle>서버</SectionTitle>
            <Card className="flex flex-col gap-1.5 text-sm">
              <InfoRow label="Node" value={data.server.node} />
              <InfoRow label="가동 시간" value={formatUptime(data.server.uptimeSec)} />
              <InfoRow
                label="메모리"
                value={`RSS ${formatBytes(data.server.memory.rss)} · 힙 ${formatBytes(data.server.memory.heapUsed)} / ${formatBytes(data.server.memory.heapTotal)}`}
              />
              <InfoRow label="환경" value={data.server.env} />
            </Card>
          </section>

          <section>
            <SectionTitle>연동 상태</SectionTitle>
            <Card className="flex flex-col gap-2">
              <IntegrationRow label="파일 저장소" ok={data.integrations.storage === 'r2'} okText="Cloudflare R2" offText="로컬 디스크(개발)" />
              <IntegrationRow label="문자·알림톡" ok={data.integrations.sms === 'solapi'} okText="Solapi 연결됨" offText="콘솔 출력(개발)" />
              <IntegrationRow label="이메일" ok={data.integrations.mail === 'smtp'} okText="SMTP 연결됨" offText="콘솔 출력(개발)" />
              <IntegrationRow label="앱 푸시" ok={data.integrations.push} okText="FCM 설정됨" offText="미설정" />
              <IntegrationRow label="웹푸시(브라우저·PWA)" ok={data.integrations.webPush} okText="VAPID 설정됨" offText="미설정" />
              <IntegrationRow label="카카오 로그인" ok={data.integrations.kakaoLogin} okText="설정됨" offText="미설정" />
              <IntegrationRow label="본인인증(포트원)" ok={data.integrations.identity} okText="설정됨" offText="미설정" />
              <IntegrationRow label="주소 검색(지오코딩)" ok={data.integrations.geocoding} okText="설정됨" offText="미설정" />
              <IntegrationRow label="AI 도우미" ok={data.integrations.ai} okText="설정됨" offText="미설정" />
              <IntegrationRow label="Sentry" ok={data.integrations.sentry} okText="설정됨" offText="미설정" />
            </Card>
          </section>

          <MaintenanceSection />

          <Button variant="outline" block icon={<LogOut size={18} />} onClick={() => void logout()}>
            로그아웃
          </Button>
        </div>
      )}
    </Screen>
  );
}

function InfoRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <span className="shrink-0 text-ink-sub">{label}</span>
      <span className="min-w-0 truncate text-right font-medium">{value}</span>
    </div>
  );
}

function IntegrationRow({
  label,
  ok,
  okText,
  offText,
}: {
  label: string;
  ok: boolean;
  okText: string;
  offText: string;
}) {
  return (
    <div className="flex items-center justify-between gap-3 text-sm">
      <span className="text-ink-sub">{label}</span>
      <Chip tone={ok ? 'primary' : 'neutral'}>{ok ? okText : offText}</Chip>
    </div>
  );
}

/** 유지보수 — 되돌릴 수 있고 데이터가 남는 작업만 버튼으로 둔다 */
function MaintenanceSection() {
  const queryClient = useQueryClient();
  const [result, setResult] = useState<string | null>(null);

  const retry = useMutation({
    mutationFn: () => apiData<{ requeued: number }>('/admin/system/retry-notifications', { method: 'POST' }),
    onSuccess: (data) => {
      setResult(`실패한 발송 ${formatCount(data.requeued)}건을 대기 상태로 되돌렸어요. 다음 주기에 다시 발송됩니다.`);
      void queryClient.invalidateQueries({ queryKey: ['admin', 'overview'] });
    },
  });

  const cleanup = useMutation({
    mutationFn: () =>
      apiData<{ refreshTokens: number; phoneVerifications: number; passwordResets: number }>(
        '/admin/system/cleanup',
        { method: 'POST' },
      ),
    onSuccess: (data) => {
      const total = data.refreshTokens + data.phoneVerifications + data.passwordResets;
      setResult(
        `만료 데이터 ${formatCount(total)}건을 정리했어요 (세션 ${data.refreshTokens} · 인증코드 ${data.phoneVerifications} · 재설정 토큰 ${data.passwordResets}).`,
      );
      void queryClient.invalidateQueries({ queryKey: ['admin', 'system'] });
    },
  });

  return (
    <section>
      <SectionTitle>유지보수</SectionTitle>
      <Card className="flex flex-col gap-3">
        <div className="flex flex-wrap gap-2">
          <Button size="sm" variant="outline" loading={retry.isPending} onClick={() => retry.mutate()}>
            실패 알림 재발송
          </Button>
          <Button
            size="sm"
            variant="outline"
            loading={cleanup.isPending}
            onClick={() => {
              if (window.confirm('만료된 세션·인증코드·재설정 토큰을 정리할까요? 사용 중인 계정에는 영향이 없어요.')) {
                cleanup.mutate();
              }
            }}
          >
            만료 데이터 정리
          </Button>
        </div>
        {result && (
          <p className="rounded-sm bg-primary-soft px-3 py-2 text-sm text-primary-strong">{result}</p>
        )}
        <p className="text-sm text-ink-sub">
          스키마 변경과 백업·복구는 콘솔 밖(마이그레이션·호스팅 대시보드)에서 다뤄요. 여기서는 데이터가
          사라지지 않는 작업만 제공합니다.
        </p>
      </Card>
    </section>
  );
}
